/**
 * Box Truck Bid Analytics — cross-tenant staff view of the bid funnel.
 *
 * Drivers place bids from the mobile app; dispatchers answer them by emailing the broker from the admin
 * loadboard. This page puts the two side by side per COMPANY and shows the gap between them: how many bids
 * the fleet placed, how many bid emails the team sent, and what share of the bids actually got an email.
 * Click a company for its per-driver / per-dispatcher breakdown and its newest bids.
 *
 * Backend: GET /v1/gluon/box-truck-bid-analytics (any control-panel operator). Read-only.
 */
import * as React from 'react'
import axios from 'axios'
import { ANALYTICS_WINDOW_DAYS, ANALYTICS_WINDOW_LABEL, type CompanyRecentBidRow } from '@numeo/types-main-mobile'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import Input from '@mui/joy/Input'
import LinearProgress from '@mui/joy/LinearProgress'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import TabPanel from '@mui/joy/TabPanel'
import Table from '@mui/joy/Table'
import Tabs from '@mui/joy/Tabs'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import BusinessIcon from '@mui/icons-material/Business'
import GavelIcon from '@mui/icons-material/Gavel'
import InsightsIcon from '@mui/icons-material/Insights'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import { fmtDate, StatCard } from '../_shared/StatCard'

interface CompanyRow {
    applicationId: string
    projectName?: string
    companyName?: string
    ownerEmail?: string
    claimedDrivers: number
    biddingDrivers: number
    driverBids: number
    bidLoads: number
    bidEmails: number
    emailedLoads: number
    dispatchers: number
    emailedBids: number
    coveragePct: number | null
    lastBidAt: string | null
    lastEmailAt: string | null
}

interface Summary {
    range: { from: string | null; days: number | null }
    totals: {
        companies: number
        claimedDrivers: number
        biddingDrivers: number
        driverBids: number
        bidEmails: number
        emailedBids: number
        coveragePct: number | null
    }
    unclaimed: { bids: number; bidders: number }
    companies: CompanyRow[]
    pagination: { page: number; limit: number; total: number }
}

interface DriverRow {
    userId: string
    name: string
    email?: string
    bids: number
    emailedBids: number
    coveragePct: number | null
    lastBidAt: string | null
}

interface DispatcherRow {
    dispatcherId: string
    email?: string
    emails: number
    loads: number
    lastEmailAt: string | null
}

interface Detail {
    company: CompanyRow
    drivers: DriverRow[]
    dispatchers: DispatcherRow[]
    recentBids: CompanyRecentBidRow[]
}

/** The server's own set (`@numeo/types`), so the selector and the router cannot drift apart. */
const RANGES: { value: number; label: string }[] = ANALYTICS_WINDOW_DAYS.map((value) => ({ value, label: ANALYTICS_WINDOW_LABEL[value] }))

const fmtNum = (value: number): string => value.toLocaleString()

const PAGE_SIZE = 50
const companyLabel = (c: Pick<CompanyRow, 'companyName' | 'projectName' | 'applicationId'>): string => c.companyName || c.projectName || c.applicationId

/** Coverage tone: how much of the fleet's bidding the team actually answered. */
const coverageTone = (v: number | null): 'neutral' | 'danger' | 'warning' | 'success' => {
    if (v === null) return 'neutral'
    if (v < 25) return 'danger'
    if (v < 60) return 'warning'
    return 'success'
}

/** The page's core visual: answered / total as a bar plus the percentage, tone-coded. */
function CoverageBar({ pct, answered, total, width = 120 }: { pct: number | null; answered: number; total: number; width?: number }) {
    const tone = coverageTone(pct)
    if (pct === null) {
        return (
            <Typography level="body-xs" textColor="text.tertiary">
                no bids
            </Typography>
        )
    }
    return (
        <Tooltip title={`${fmtNum(answered)} of ${fmtNum(total)} bids answered by email`} placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: width }}>
                <LinearProgress determinate value={Math.min(100, pct)} color={tone} thickness={8} sx={{ flex: 1, '--LinearProgress-radius': '6px' }} />
                <Typography level="body-xs" sx={{ fontWeight: 600, minWidth: 42, textAlign: 'right' }} textColor={`${tone}.plainColor`}>
                    {pct}%
                </Typography>
            </Box>
        </Tooltip>
    )
}

export default function BoxTruckBidAnalyticsPage() {
    const [days, setDays] = React.useState<number>(30)
    const [summary, setSummary] = React.useState<Summary | null>(null)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const [searchInput, setSearchInput] = React.useState('')
    const [search, setSearch] = React.useState('')
    const [page, setPage] = React.useState(1)

    const [selected, setSelected] = React.useState<CompanyRow | null>(null)
    const [detail, setDetail] = React.useState<Detail | null>(null)
    const [detailLoading, setDetailLoading] = React.useState(false)
    const [detailError, setDetailError] = React.useState<string | null>(null)

    const load = React.useCallback(async (targetPage: number, range: number, searchTerm: string) => {
        setLoading(true)
        setError(null)
        try {
            const res = await axios.get<{ success: boolean; data: Summary }>('/gluon/box-truck-bid-analytics', {
                params: { days: range, page: targetPage, limit: PAGE_SIZE, ...(searchTerm ? { search: searchTerm } : {}) },
            })
            setSummary(res.data.data)
            setPage(targetPage)
        } catch (e) {
            console.error('[box-truck-bid-analytics] summary load failed', e)
            setError(e instanceof Error ? e.message : 'Failed to load bid analytics')
        } finally {
            setLoading(false)
        }
    }, [])

    // Debounce the search box → the committed term drives the fetch (server-side, so matches on later pages
    // are never hidden). A new range or term always restarts at page 1.
    React.useEffect(() => {
        const t = window.setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => window.clearTimeout(t)
    }, [searchInput])

    React.useEffect(() => {
        void load(1, days, search)
    }, [load, days, search])

    // Opening a company (or changing the range while one is open) pulls its breakdown.
    React.useEffect(() => {
        if (!selected) {
            setDetail(null)
            return
        }
        let cancelled = false
        setDetail(null)
        setDetailLoading(true)
        setDetailError(null)
        axios
            .get<{ success: boolean; data: Detail }>(`/gluon/box-truck-bid-analytics/${selected.applicationId}`, { params: { days } })
            .then((res) => {
                if (!cancelled) setDetail(res.data.data)
            })
            .catch((e) => {
                console.error('[box-truck-bid-analytics] company load failed', e)
                if (!cancelled) setDetailError(e instanceof Error ? e.message : 'Failed to load the company breakdown')
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [selected, days])

    const rows = summary?.companies ?? []
    const totals = summary?.totals
    const matched = summary?.pagination.total ?? 0
    const totalPages = Math.max(1, Math.ceil(matched / PAGE_SIZE))
    const rangeLabel = RANGES.find((r) => r.value === days)?.label ?? ''

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography level="h3" startDecorator={<InsightsIcon />}>
                        Box Truck Bid Analytics
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        Every box-truck company: what its drivers bid on, what its dispatchers emailed, and how much of the bidding actually got answered
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Select size="sm" value={days} onChange={(_, v) => setDays((v as number) ?? 30)} sx={{ minWidth: 150 }}>
                        {RANGES.map((r) => (
                            <Option key={r.value} value={r.value}>
                                {r.label}
                            </Option>
                        ))}
                    </Select>
                    <Button variant="outlined" size="sm" startDecorator={<RefreshIcon />} onClick={() => void load(page, days, search)} loading={loading}>
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Platform totals */}
            {totals && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <StatCard
                        icon={<BusinessIcon />}
                        label="Companies"
                        value={fmtNum(totals.companies)}
                        hint={`${fmtNum(totals.claimedDrivers)} claimed drivers`}
                    />
                    <StatCard
                        icon={<GavelIcon />}
                        label="Driver bids"
                        value={fmtNum(totals.driverBids)}
                        hint={`${fmtNum(totals.biddingDrivers)} drivers bidding`}
                        tone="warning"
                    />
                    <StatCard icon={<ForwardToInboxIcon />} label="Bid emails sent" value={fmtNum(totals.bidEmails)} hint="by dispatchers" tone="primary" />
                    <StatCard
                        icon={<MarkEmailReadIcon />}
                        label="Bids answered by email"
                        value={fmtNum(totals.emailedBids)}
                        hint={totals.coveragePct === null ? undefined : `${totals.coveragePct}% coverage`}
                        tone={coverageTone(totals.coveragePct)}
                    />
                </Box>
            )}

            {summary && summary.unclaimed.bids > 0 && (
                <Alert variant="soft" color="neutral" size="sm">
                    <Typography level="body-sm">
                        {fmtNum(summary.unclaimed.bids)} bids from {fmtNum(summary.unclaimed.bidders)} drivers no company has claimed — those bids appear on no
                        operator&apos;s Driver Bids tab, so nobody can answer them.
                    </Typography>
                </Alert>
            )}

            {error && (
                <Alert variant="soft" color="danger">
                    {error}
                </Alert>
            )}

            {/* Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Input
                    size="sm"
                    placeholder="Filter by company, project or owner"
                    startDecorator={<SearchIcon />}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{ minWidth: 300 }}
                />
                <Typography level="body-xs" textColor="text.tertiary">
                    {search ? `${fmtNum(matched)} of ${fmtNum(totals?.companies ?? 0)} companies match` : `${fmtNum(matched)} companies`} · {rangeLabel}
                </Typography>
            </Box>

            {/* Companies */}
            <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
                <Table hoverRow stickyHeader sx={{ '--TableCell-paddingX': '12px', minWidth: 1040 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 260 }}>Company</th>
                            <th style={{ width: 110 }}>Fleet</th>
                            <th style={{ width: 100 }}>Driver bids</th>
                            <th style={{ width: 110 }}>Bid emails</th>
                            <th style={{ width: 100 }}>Answered</th>
                            <th style={{ width: 170 }}>Coverage</th>
                            <th style={{ width: 180 }}>Last bid</th>
                            <th style={{ width: 180 }}>Last email</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !summary && (
                            <tr>
                                <td colSpan={8}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={8}>
                                    <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 3, textAlign: 'center' }}>
                                        No box-truck companies match this filter.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                        {rows.map((c) => (
                            <tr key={c.applicationId} style={{ cursor: 'pointer' }} onClick={() => setSelected(c)}>
                                <td>
                                    <Typography level="body-sm" sx={{ fontWeight: 600 }} noWrap>
                                        {companyLabel(c)}
                                    </Typography>
                                    <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                        {c.ownerEmail || c.projectName || '—'}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-sm">{fmtNum(c.claimedDrivers)}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        {fmtNum(c.biddingDrivers)} bidding
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                        {fmtNum(c.driverBids)}
                                    </Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        {fmtNum(c.bidLoads)} loads
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                        {fmtNum(c.bidEmails)}
                                    </Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        {fmtNum(c.dispatchers)} dispatchers
                                    </Typography>
                                </td>
                                <td>
                                    <Typography level="body-sm">{fmtNum(c.emailedBids)}</Typography>
                                </td>
                                <td>
                                    <CoverageBar pct={c.coveragePct} answered={c.emailedBids} total={c.driverBids} />
                                </td>
                                <td>
                                    <Typography level="body-xs">{fmtDate(c.lastBidAt)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{fmtDate(c.lastEmailAt)}</Typography>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <Button size="sm" variant="outlined" disabled={page <= 1 || loading} onClick={() => void load(page - 1, days, search)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page >= totalPages || loading} onClick={() => void load(page + 1, days, search)}>
                        Next
                    </Button>
                </Box>
            )}

            {/* Company drill-down */}
            <Modal open={!!selected} onClose={() => setSelected(null)}>
                <ModalDialog sx={{ width: 'min(1100px, 96vw)', maxHeight: '92vh', overflow: 'auto' }}>
                    <ModalClose />
                    {selected && (
                        <>
                            <Typography level="h4" startDecorator={<BusinessIcon />}>
                                {companyLabel(selected)}
                            </Typography>
                            <Typography level="body-xs" textColor="text.tertiary">
                                {selected.projectName || '—'}
                                {selected.ownerEmail ? ` · ${selected.ownerEmail}` : ''} · {rangeLabel}
                            </Typography>

                            <Sheet variant="soft" sx={{ borderRadius: 'md', p: 2, mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box>
                                    <Typography level="h3">{fmtNum(selected.driverBids)}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        driver bids
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography level="h3">{fmtNum(selected.bidEmails)}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        bid emails sent · {fmtNum(selected.emailedLoads)} loads
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography level="h3">{fmtNum(selected.emailedBids)}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        bids answered by email
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <CoverageBar pct={selected.coveragePct} answered={selected.emailedBids} total={selected.driverBids} width={200} />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        coverage
                                    </Typography>
                                </Box>
                            </Sheet>

                            {detailError && (
                                <Alert variant="soft" color="danger" sx={{ mt: 1.5 }}>
                                    {detailError}
                                </Alert>
                            )}
                            {detailLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress size="sm" />
                                </Box>
                            )}

                            {detail && !detailLoading && (
                                <Tabs defaultValue={0} sx={{ mt: 1.5, bgcolor: 'transparent' }}>
                                    <TabList>
                                        <Tab value={0}>Drivers ({detail.drivers.length})</Tab>
                                        <Tab value={1}>Dispatchers ({detail.dispatchers.length})</Tab>
                                        <Tab value={2}>Recent bids ({detail.recentBids.length})</Tab>
                                    </TabList>

                                    <TabPanel value={0} sx={{ px: 0 }}>
                                        <Table size="sm" hoverRow sx={{ '--TableCell-paddingX': '10px' }}>
                                            <thead>
                                                <tr>
                                                    <th>Driver</th>
                                                    <th style={{ width: 90 }}>Bids</th>
                                                    <th style={{ width: 100 }}>Answered</th>
                                                    <th style={{ width: 160 }}>Coverage</th>
                                                    <th style={{ width: 180 }}>Last bid</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.drivers.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5}>
                                                            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                                                                None of this company&apos;s drivers bid in this window.
                                                            </Typography>
                                                        </td>
                                                    </tr>
                                                )}
                                                {detail.drivers.map((d) => (
                                                    <tr key={d.userId}>
                                                        <td>
                                                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                                {d.name || '—'}
                                                            </Typography>
                                                            <Typography level="body-xs" textColor="text.tertiary">
                                                                {d.email || d.userId}
                                                            </Typography>
                                                        </td>
                                                        <td>{fmtNum(d.bids)}</td>
                                                        <td>{fmtNum(d.emailedBids)}</td>
                                                        <td>
                                                            <CoverageBar pct={d.coveragePct} answered={d.emailedBids} total={d.bids} width={110} />
                                                        </td>
                                                        <td>
                                                            <Typography level="body-xs">{fmtDate(d.lastBidAt)}</Typography>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </TabPanel>

                                    <TabPanel value={1} sx={{ px: 0 }}>
                                        <Table size="sm" hoverRow sx={{ '--TableCell-paddingX': '10px' }}>
                                            <thead>
                                                <tr>
                                                    <th>Dispatcher</th>
                                                    <th style={{ width: 120 }}>Bid emails</th>
                                                    <th style={{ width: 120 }}>Loads emailed</th>
                                                    <th style={{ width: 200 }}>Last email</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.dispatchers.length === 0 && (
                                                    <tr>
                                                        <td colSpan={4}>
                                                            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                                                                No dispatcher on this team sent a bid email in this window.
                                                            </Typography>
                                                        </td>
                                                    </tr>
                                                )}
                                                {detail.dispatchers.map((d) => (
                                                    <tr key={d.dispatcherId}>
                                                        <td>
                                                            <Typography level="body-sm">{d.email || d.dispatcherId}</Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                                {fmtNum(d.emails)}
                                                            </Typography>
                                                        </td>
                                                        <td>{fmtNum(d.loads)}</td>
                                                        <td>
                                                            <Typography level="body-xs">{fmtDate(d.lastEmailAt)}</Typography>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </TabPanel>

                                    <TabPanel value={2} sx={{ px: 0 }}>
                                        <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                                            Newest bids first. &quot;Answered&quot; means this team emailed the broker on that load at or after the price shown
                                            was asked for. A driver who moved their price shows the later time, since the earlier email did not answer this
                                            figure.
                                        </Typography>
                                        <Table size="sm" hoverRow sx={{ '--TableCell-paddingX': '10px' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 170 }}>Driver</th>
                                                    <th>Lane</th>
                                                    <th style={{ width: 100 }}>Bid</th>
                                                    <th style={{ width: 180 }}>Placed</th>
                                                    <th style={{ width: 240 }}>Answered</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.recentBids.length === 0 && (
                                                    <tr>
                                                        <td colSpan={5}>
                                                            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                                                                No bids in this window.
                                                            </Typography>
                                                        </td>
                                                    </tr>
                                                )}
                                                {detail.recentBids.map((b) => (
                                                    <tr key={`${b.source}|${b.loadId}:${b.bidderId}`}>
                                                        <td>
                                                            <Typography level="body-sm" noWrap>
                                                                {b.bidderName || '—'}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-sm" noWrap>
                                                                {b.lane || '—'}
                                                            </Typography>
                                                            <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                                                {[b.source, b.loadId].filter(Boolean).join(' · ')}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                                ${fmtNum(Math.round(b.price))}
                                                            </Typography>
                                                        </td>
                                                        <td>
                                                            <Typography level="body-xs">{fmtDate(b.askedAt)}</Typography>
                                                            {b.askedAt !== b.createdAt && (
                                                                <Typography level="body-xs" textColor="text.tertiary">
                                                                    first bid {fmtDate(b.createdAt)}
                                                                </Typography>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {b.emailedAt ? (
                                                                <Tooltip title={b.emailedByEmail || 'a dispatcher'} placement="top">
                                                                    <Chip size="sm" variant="soft" color="success" startDecorator={<MarkEmailReadIcon />}>
                                                                        {fmtDate(b.emailedAt)}
                                                                    </Chip>
                                                                </Tooltip>
                                                            ) : (
                                                                <Chip size="sm" variant="soft" color="neutral">
                                                                    no email
                                                                </Chip>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </TabPanel>
                                </Tabs>
                            )}

                            <Divider sx={{ mt: 2 }} />
                            <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 1 }}>
                                Bids attribute through the company&apos;s claimed fleet; bid emails through the team&apos;s project — the same rules the
                                operator&apos;s own Driver Bids and Bid-loads tabs use.
                            </Typography>
                        </>
                    )}
                </ModalDialog>
            </Modal>
        </Box>
    )
}
